import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import RotateRightIcon from "@mui/icons-material/RotateRight";

const PRIMARY = "#7B6ED6";

const getFileType = (file, fileName) => {
  const src = file || fileName || "";
  const ext = src.split("?")[0].split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext))
    return "image";
  if (ext === "pdf") return "pdf";
  return "other";
};

const isBase64 = (str) => typeof str === "string" && str.startsWith("data:");

const getDisplayUrl = (file) => {
  if (!file || file === "No file") return null;
  return file;
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const ImageViewer = ({ src, alt }) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const dragStart = useRef(null);
  const containerRef = useRef(null);

  const clampScale = (s) => Math.min(Math.max(s, 0.5), 6);

  const zoom = (delta) => {
    setScale((prev) => clampScale(prev + delta));
  };

  const reset = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setRotation(0);
  };

  const rotate = () => setRotation((r) => (r + 90) % 360);

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setScale((prev) => clampScale(prev + delta));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      if (el) el.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);

  // Mouse drag
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onMouseMove = (e) => {
    if (!isDragging || !dragStart.current) return;
    setOffset({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };
  const onMouseUp = () => {
    setIsDragging(false);
    dragStart.current = null;
  };

  // Touch pinch-zoom
  const lastTouchDist = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouchDist.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const delta = (dist - lastTouchDist.current) * 0.01;
      setScale((prev) => clampScale(prev + delta));
      lastTouchDist.current = dist;
    }
  };
  const onTouchEnd = () => {
    lastTouchDist.current = null;
  };

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
          gap: 1,
          color: "text.secondary",
        }}
      >
        <ImageIcon sx={{ fontSize: 48, opacity: 0.4 }} />
        <Typography variant="body2">Unable to load image</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "#fafafa",
        }}
      >
        <Tooltip title="Zoom out">
          <IconButton
            size="small"
            onClick={() => zoom(-0.25)}
            disabled={scale <= 0.5}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            minWidth: 52,
            textAlign: "center",
            fontSize: "0.78rem",
            fontWeight: 600,
            color: PRIMARY,
            userSelect: "none",
          }}
        >
          {Math.round(scale * 100)}%
        </Box>

        <Tooltip title="Zoom in">
          <IconButton
            size="small"
            onClick={() => zoom(0.25)}
            disabled={scale >= 6}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Rotate 90°">
          <IconButton size="small" onClick={rotate}>
            <RotateRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Reset view">
          <IconButton size="small" onClick={reset}>
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Typography variant="caption" sx={{ color: "text.disabled", mr: 1 }}>
          Scroll or pinch to zoom · Drag to pan
        </Typography>
      </Box>

      {/* Image Stage */}
      <Box
        ref={containerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          width: "100%",
          height: { xs: 320, sm: 480 },
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#1a1a2e",
          cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          position: "relative",
          userSelect: "none",
        }}
      >
        {!loaded && (
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0 }}>
            <LinearProgress
              sx={{ "& .MuiLinearProgress-bar": { backgroundColor: PRIMARY } }}
            />
          </Box>
        )}
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transition: isDragging ? "none" : "transform 0.15s ease",
            display: loaded ? "block" : "none",
            willChange: "transform",
          }}
        />
        {!loaded && !error && (
          <Box sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.85rem" }}>
            Loading…
          </Box>
        )}
      </Box>
    </Box>
  );
};

const PdfViewer = ({ src, fileName }) => {
  const [pdfFailed, setPdfFailed] = useState(false);

  // For base64 PDFs, create an object URL
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    if (isBase64(src) && src.startsWith("data:application/pdf")) {
      const byteString = atob(src.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++)
        ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [src]);

  const displaySrc = objectUrl || src;

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = displaySrc;
    a.download = fileName || "document.pdf";
    a.click();
  };

  if (pdfFailed) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
          gap: 2,
          color: "text.secondary",
          p: 3,
        }}
      >
        <PictureAsPdfIcon
          sx={{ fontSize: 56, color: "#e74c3c", opacity: 0.7 }}
        />
        <Typography variant="body2" align="center">
          Your browser couldn't display this PDF inline.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ backgroundColor: PRIMARY, textTransform: "none" }}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(displaySrc, "_blank")}
            sx={{ textTransform: "none" }}
          >
            Open in new tab
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      <iframe
        src={displaySrc}
        title={fileName || "PDF Document"}
        width="100%"
        style={{
          height: "min(70vh, 600px)",
          border: "none",
          display: "block",
          backgroundColor: "#525659",
        }}
        onError={() => setPdfFailed(true)}
      />
      {/* Overlay download button */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 0.5,
        }}
      >
        <Tooltip title="Download">
          <IconButton
            size="small"
            onClick={handleDownload}
            sx={{
              backgroundColor: "rgba(0,0,0,0.55)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in new tab">
          <IconButton
            size="small"
            onClick={() => window.open(displaySrc, "_blank")}
            sx={{
              backgroundColor: "rgba(0,0,0,0.55)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const OtherFileViewer = ({ src, fileName }) => {
  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = src;
    a.download = fileName || "document";
    a.target = "_blank";
    a.click();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: 260,
        gap: 2,
        color: "text.secondary",
        p: 3,
      }}
    >
      <InsertDriveFileIcon sx={{ fontSize: 64, opacity: 0.35 }} />
      <Typography variant="body2" align="center">
        Preview is not available for this file type.
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ backgroundColor: PRIMARY, textTransform: "none" }}
        >
          Download File
        </Button>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open(src, "_blank")}
          sx={{ textTransform: "none" }}
        >
          Open in new tab
        </Button>
      </Box>
    </Box>
  );
};

const NoFileView = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: 220,
      gap: 1.5,
      color: "text.secondary",
    }}
  >
    <InsertDriveFileIcon sx={{ fontSize: 56, opacity: 0.25 }} />
    <Typography variant="body2">No file attached to this document</Typography>
  </Box>
);

const DocumentViewerDialog = ({ doc, onClose }) => {
  if (!doc) return null;

  const fileUrl = getDisplayUrl(doc.file);
  const fileType = fileUrl ? getFileType(doc.file, doc.fileName) : "none";

  const typeLabel = {
    image: "Image",
    pdf: "PDF",
    other: "File",
    none: "No file",
  }[fileType];

  const typeIcon = {
    image: <ImageIcon sx={{ fontSize: 15 }} />,
    pdf: <PictureAsPdfIcon sx={{ fontSize: 15 }} />,
    other: <InsertDriveFileIcon sx={{ fontSize: 15 }} />,
    none: <InsertDriveFileIcon sx={{ fontSize: 15 }} />,
  }[fileType];

  const typeColor = {
    image: { bg: "#e8f5e9", color: "#2e7d32" },
    pdf: { bg: "#fce4ec", color: "#c62828" },
    other: { bg: "#e3f2fd", color: "#1565c0" },
    none: { bg: "#f5f5f5", color: "#757575" },
  }[fileType];

  const handleDownload = () => {
    if (!fileUrl) return;
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = doc.fileName || `${doc.name}_${doc.number}`;
    a.target = "_blank";
    a.click();
  };

  return (
    <Dialog
      open={!!doc}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          pr: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Doc name + type badge */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                color: PRIMARY,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {doc.name}
            </Typography>
            <Chip
              icon={typeIcon}
              label={typeLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 600,
                backgroundColor: typeColor.bg,
                color: typeColor.color,
                "& .MuiChip-icon": { color: typeColor.color, ml: "6px" },
              }}
            />
          </Box>

          {/* Meta row */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <strong>Number:</strong> {doc.number}
            </Typography>
            {doc.renewalDate && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <strong>Renewal:</strong> {formatDate(doc.renewalDate)}
              </Typography>
            )}
            {doc.fileName && doc.fileName !== "null" && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  maxWidth: 260,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {doc.fileName}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton size="small" onClick={onClose} sx={{ mt: 0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {fileType === "none" && <NoFileView />}
        {fileType === "image" && <ImageViewer src={fileUrl} alt={doc.name} />}
        {fileType === "pdf" && (
          <PdfViewer src={fileUrl} fileName={doc.fileName || doc.name} />
        )}
        {fileType === "other" && (
          <OtherFileViewer src={fileUrl} fileName={doc.fileName || doc.name} />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "divider",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {fileType === "image"
            ? "Use scroll wheel or pinch to zoom. Drag to pan when zoomed."
            : fileType === "pdf"
            ? "PDF rendered inline. Use toolbar controls inside viewer."
            : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {fileUrl && fileType !== "none" && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                textTransform: "none",
                borderColor: PRIMARY,
                color: PRIMARY,
              }}
            >
              Download
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={onClose}
            sx={{
              textTransform: "none",
              backgroundColor: PRIMARY,
            }}
          >
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewerDialog;
